import React, { useState, useMemo } from 'react';
import { StyleSheet, ScrollView, TextInput, View, Pressable, Image, Linking, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useChat } from '@/hooks/useChat';
import { useTranslation } from '@/hooks/useTranslation';
import ContextualSidebarHeader from '@/components/Sidebar/ContextualSidebarHeader';
import MediaCard from '@/components/ChatInput/MediaCard';
import { useUIStore, useModalStore, useLightboxStore } from '@/store';
import { useModalHelper } from '@/hooks/useModalHelper';
import { openUploadedChatFile } from '@/constants/utils/files';
import ProjectChangesSection from '@/components/Chat/ProjectChangesSection';

const createStyles = (colors, isModal) => StyleSheet.create({
  container: {
    width: '100%',
    // On native inside a BottomSheetView, height:'100%' overflows the padded area
    // and gets clipped.  flex:1 properly fills only the available space.
    ...(Platform.OS === 'web' ? { height: '100%' } : { flex: 1 }),
    backgroundColor: isModal ? colors.solid_color_block : colors.solid_color_primary,
  },
  header: {
    height: 53,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.border_color_secondary,
  },
  closeButton: {
    padding: 8,
    marginRight: -8,
    borderRadius: 6,
  },
  content: {
    flex: 1,
    // On native inside a bottom-sheet modal, flexBasis:0 + minHeight:0
    // are required so BottomSheetScrollView receives proper height.
    ...(Platform.OS !== 'web' && { flexBasis: 0, minHeight: 0 }),
  },
  scrollContent: {
    padding: isModal ? 16 : 16,
    paddingVertical: isModal ? 20 : 16,
    paddingTop: isModal ? 0 : 16,
  },
  searchContainer: {
    marginBottom: 20,
    position: 'relative',
    backgroundColor: colors.solid_color_tertiary,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    borderRadius: 8,
    height: 36,
  },
  searchInput: {
    height: '100%',
    backgroundColor: 'transparent',
    paddingLeft: 36,
    paddingRight: 12,
    color: colors.text_color_primary,
    fontSize: 14,
  },
  searchIcon: {
    position: 'absolute',
    left: 12,
    top: 10,
  },
  section: {
    marginBottom: isModal ? 26 : 24,
  },
  sectionTitle: {
    fontSize: isModal ? 12 : 13,
    fontWeight: isModal ? '400' : '600',
    color: colors.text_color_secondary,
    marginBottom: 8,
    marginLeft: isModal ? 16 : 0,
    textTransform: isModal ? 'uppercase' : 'none',
  },
  sectionContent: {
    borderRadius: isModal ? 10 : 0,
    overflow: 'hidden',
    borderWidth: isModal ? 0.5 : 0,
    borderColor: colors.border_color_secondary,
  },
  emptyContainer: {
    padding: 12,
  },
  mediaButton: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 36,
    padding: 12,
    backgroundColor: isModal ? colors.solid_color_primary : colors.solid_color_tertiary,
    borderRadius: isModal ? 0 : 8,
    marginBottom: isModal ? 0 : 8,
    borderBottomWidth: isModal ? 0.5 : 0,
    borderBottomColor: colors.border_color_secondary,
  },
  mediaButtonListItem: {
    backgroundColor: 'transparent',
    borderBottomWidth: 1,
    borderBottomColor: colors.border_color_secondary,
    marginBottom: 0,
    borderRadius: 0,
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 36,
    paddingVertical: 12,
    paddingHorizontal: 0,
  },
  mediaButtonLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
  },
  mediaButtonRight: {
    marginLeft: 'auto',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
  },
  mediaThumb: {
    width: isModal ? 48 : 56,
    height: isModal ? 48 : 56,
    borderRadius: 8,
    backgroundColor: colors.solid_color_tertiary,
  },
  tagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  tag: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    gap: 4,
  },
  tagText: {
    fontSize: 12,
    color: colors.text_color_primary,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 12,
    backgroundColor: colors.solid_color_tertiary,
    borderRadius: 8,
    marginBottom: 8,
  },
  actionButtonLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  actionButtonTitle: {
    fontSize: 14,
    color: colors.text_color_primary,
  },
  infoItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 12,
    backgroundColor: colors.solid_color_tertiary,
    borderRadius: 8,
    marginBottom: 8,
  },
  infoLabel: {
    fontSize: 14,
    color: colors.text_color_secondary,
  },
  infoValue: {
    fontSize: 14,
    color: colors.text_color_primary,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  fileRow: {
    marginBottom: 8,
  },
  linkItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: colors.border_color_secondary,
  },
  linkText: {
    fontSize: 14,
    color: colors.text_color_primary,
  },
  savedItem: {
    padding: 12,
    backgroundColor: colors.solid_color_tertiary,
    borderRadius: 8,
    marginBottom: 8,
    ...(Platform.OS === 'web' && { cursor: 'pointer' }),
  },
});

const MediaButton = ({ icon, title, rightText, onPress, isModal, variant = 'default', isLastItem }) => {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors, isModal), [colors, isModal]);

  const isListVariant = variant === 'list';

  return (
    <Pressable 
      style={({ hovered }) => [
        !isListVariant && styles.mediaButton,
        isListVariant && styles.mediaButtonListItem,
        isListVariant && isLastItem && { borderBottomWidth: 0 },
        hovered && !isListVariant && { backgroundColor: colors.solid_color_secondary },
      ]}
      onPress={onPress}
    >
      <View style={styles.mediaButtonLeft}>
        {icon && <Icon name={icon} size={14} color={colors.text_color_primary} />}
        <Text>{title}</Text>
      </View>
      <View style={styles.mediaButtonRight}>
        {typeof rightText !== 'undefined' && (
          <Text color='tertiary'>{rightText}</Text>
        )}
        {onPress && <Icon name="angle-right" size={14} color={colors.text_color_tertiary} />}
      </View>
    </Pressable>
  );
};

function ChatDetailsPageView({ styles, emptyText, isEmpty, children, ScrollComponent = ScrollView }) {
  return (
    <ScrollComponent style={styles.content} contentContainerStyle={styles.scrollContent}>
      <View style={styles.sectionContent}>
        {isEmpty && (
          <View style={styles.emptyContainer}>
            <Text color='tertiary'>{emptyText}</Text>
          </View>
        )}
        {!isEmpty && children}
      </View>
    </ScrollComponent>
  );
}

function ChatDetailsSidebar({ onClose, isModal, conversationId, hideHeader = false }) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const styles = useMemo(() => createStyles(colors, isModal), [colors, isModal]);
  const [activeView, setActiveView] = useState('main'); // 'main' | 'photos' | 'videos' | 'files' | 'links' | 'saved'
  const ScrollComponent = ScrollView;
  const requestScrollToMessage = useUIStore(state => state.requestScrollToMessage);
  const setHighlightedMessageId = useUIStore(state => state.setHighlightedMessageId);
  const { openModal } = useModalStore(state => state.actions);
  const { openLightbox } = useLightboxStore(state => state.actions);
  const { useConfirmationModal } = useModalHelper();
  const showConfirmationModal = useConfirmationModal();

  // Responsive photo grid sizing
  const [photoGridWidth, setPhotoGridWidth] = useState(0);
  const photoThumbGap = 8;
  const photoGridContentWidth = Math.max(0, photoGridWidth - (photoThumbGap * 2));
  const photoMinSize = isModal ? 56 : 64;
  const photoNumColumns = useMemo(() => {
    if (photoGridContentWidth <= 0) return isModal ? 4 : 5;
    const cols = Math.floor((photoGridContentWidth + photoThumbGap) / (photoMinSize + photoThumbGap));
    return Math.max(2, Math.min(cols, 8));
  }, [photoGridContentWidth, photoMinSize, photoThumbGap, isModal]);
  const photoThumbSize = useMemo(() => {
    if (photoGridContentWidth <= 0) return isModal ? 48 : 56;
    const totalGaps = photoThumbGap * (photoNumColumns - 1);
    return Math.floor((photoGridContentWidth - totalGaps) / photoNumColumns);
  }, [photoGridContentWidth, photoNumColumns, photoThumbGap, isModal]);

  // Load chat data
  const {
    chat,
  } = useChat(conversationId);

  // Derive media and info
  const { photos, videos, audios, files, links, savedCount, modelsUsed, savedMessages } = useMemo(() => {
    const result = { photos: [], videos: [], audios: [], files: [], links: [], savedCount: 0, modelsUsed: [], savedMessages: [] };
    if (!chat?.messages) return result;

    const linkRegex = /(https?:\/\/[^\s)]+)|(www\.[^\s)]+)/gi;
    const modelNames = new Set();
    const seenFileKeys = new Set();

    chat.messages.forEach(m => {
      if (m?.flags?.isSaved) result.savedMessages.push(m);

      // Collect models used
      if (Array.isArray(m?.models)) {
        m.models.forEach(mod => {
          if (mod?.name) modelNames.add(mod.name);
        });
      }

      // Files (images, videos, audio and other files)
      if (Array.isArray(m?.files)) {
        m.files.forEach(f => {
          const isImage = f?.fileType === 'image' || f?.type === 'image' || (f?.mimeType || '').startsWith('image/');
          const isVideo = f?.fileType === 'video' || f?.type === 'video' || (f?.mimeType || '').startsWith('video/');
          const isAudio = f?.fileType === 'audio' || f?.type === 'audio' || (f?.mimeType || '').startsWith('audio/');
          if (isImage) {
            result.photos.push(f);
          } else if (isVideo) {
            result.videos.push(f);
          } else if (isAudio) {
            result.audios.push(f);
          } else {
            const key = f?.id || f?.cdnPath || f?.url || f?.name;
            if (!key || !seenFileKeys.has(key)) {
              result.files.push(f);
              if (key) seenFileKeys.add(key);
            }
          }
        });
      }

      // Some payloads may place images separately
      if (Array.isArray(m?.images)) {
        m.images.forEach(img => {
          if (img) result.photos.push(img);
        });
      }

      // Some payloads may place videos separately
      if (Array.isArray(m?.videos)) {
        m.videos.forEach(v => {
          if (v) result.videos.push(v);
        });
      }

      // Extract links from text content
      if (typeof m?.content === 'string' && m.content.length > 0) {
        const found = m.content.match(linkRegex);
        if (found) {
          found.forEach(u => result.links.push(u));
        }
      }
    });

    result.modelsUsed = Array.from(modelNames);

    // De-duplicate by url for links
    result.links = Array.from(new Set(result.links));

    // Set saved count from savedMessages
    result.savedCount = result.savedMessages.length;

    return result;
  }, [chat]);

  const createdAt = chat?.createdAt ? new Date(chat.createdAt) : null;
  const messagesCount = chat?.messages?.length || 0;

  // Thumbnails from photos
  const photoThumbs = useMemo(() => {
    return (photos || []).slice(0, 8).map((p, idx) => ({
      key: (p.id || p.cdnPath || p.thumbnailPath || p.url || 'img') + '_' + idx,
      uri: p.thumbnailPath || p.cdnPath || p.url,
    })).filter(t => !!t.uri);
  }, [photos]);

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const searchResults = useMemo(() => {
    if (!normalizedQuery) return [];
    const msgs = Array.isArray(chat?.messages) ? chat.messages : [];
    return msgs.filter(m => typeof m?.content === 'string' && m.content.toLowerCase().includes(normalizedQuery));
  }, [chat, normalizedQuery]);

  const breadcrumbTitle = useMemo(() => {
    switch (activeView) {
      case 'photos':
        return ['Details', 'Photos'];
      case 'videos':
        return ['Details', 'Videos'];
      case 'files':
        return ['Details', 'Files'];
      case 'links':
        return ['Details', 'Links'];
      case 'saved':
        return ['Details', 'Saved'];
      default:
        return ['Details'];
    }
  }, [activeView]);

  const renderPhotosView = () => (
    <ChatDetailsPageView
      ScrollComponent={ScrollComponent}
      styles={styles}
      emptyText={t('chatDetails.noPhotosFound')}
      isEmpty={!photos || photos.length === 0}
    >
      <View
        style={[styles.grid, { padding: photoThumbGap }]}
        onLayout={(e) => setPhotoGridWidth(e?.nativeEvent?.layout?.width || 0)}
      >
        {(photos || []).map((p, idx) => {
          const uri = p.thumbnailPath || p.cdnPath || p.url;
          if (!uri) return null;
          return (
            <Pressable key={(p.id || uri) + '_' + idx} onPress={() => openLightbox({ imageUrl: uri, conversationId: conversationId })}>
              <Image
                source={{ uri }}
                style={[styles.mediaThumb, { width: photoThumbSize, height: photoThumbSize }]}
              />
            </Pressable>
          );
        })}
      </View>
    </ChatDetailsPageView>
  );

  const renderFilesView = () => (
    <ChatDetailsPageView
      ScrollComponent={ScrollComponent}
      styles={styles}
      emptyText={t('chatDetails.noFilesFound')}
      isEmpty={!files || files.length === 0}
    >
      <View style={styles.sectionContent}>
        {(files || []).map((f, idx) => (
          <MediaCard
            file={f}
            isSmall
            onPress={() => {
              if (!openUploadedChatFile(f)) openModal('FileEditModal', { fileId: f.id });
            }}
            key={(f.id || f.url || f.name || 'file') + '_' + idx}
          />
        ))}
      </View>
    </ChatDetailsPageView>
  );

  const renderVideosView = () => (
    <ChatDetailsPageView
      ScrollComponent={ScrollComponent}
      styles={styles}
      emptyText={t('chatDetails.noVideosFound')}
      isEmpty={!videos || videos.length === 0}
    >
      <View style={[styles.sectionContent, { flexDirection: 'row', flexWrap: 'wrap', gap: 8 }]}>
        {(videos || []).map((f, idx) => (
          <MediaCard
            file={f}
            onPress={() => openModal('FileEditModal', { fileId: f.id })}
            key={(f.id || f.url || f.name || 'video') + '_' + idx}
          />
        ))}
      </View>
    </ChatDetailsPageView>
  );

  const renderAudiosView = () => (
    <ChatDetailsPageView
      ScrollComponent={ScrollComponent}
      styles={styles}
      emptyText={t('chatDetails.noAudioFound')}
      isEmpty={!audios || audios.length === 0}
    >
      <View style={[styles.sectionContent, { flexDirection: 'row', flexWrap: 'wrap', gap: 8 }]}>
        {(audios || []).map((f, idx) => (
          <MediaCard
            file={f}
            onPress={() => openLightbox({ fileId: f.id, conversationId })}
            key={(f.id || f.url || f.name || 'audio') + '_' + idx}
          />
        ))}
      </View>
    </ChatDetailsPageView>
  );

  const renderLinksView = () => (
    <ChatDetailsPageView
      ScrollComponent={ScrollComponent}
      styles={styles}
      emptyText={t('chatDetails.noLinksFound')}
      isEmpty={!links || links.length === 0}
    >
      {(links || []).map((u, idx) => (
        <Pressable
          key={u + '_' + idx}
          style={styles.linkItem}
          onPress={() => {
            const url = (typeof u === 'string' && (u.startsWith('http://') || u.startsWith('https://'))) ? u : `https://${u}`;
            showConfirmationModal({
              title: t('chatDetails.externalLink'),
              message: t('chatDetails.externalLinkMessage'),
              itemName: url,
              confirmText: t('chatDetails.openLink'),
              cancelText: t('common.cancel'),
              onConfirm: () => {
                if (Platform.OS === 'web') {
                  window.open(url, '_blank');
                } else {
                  Linking.openURL(url);
                }
              }
            });
          }}
        >
          <Text style={styles.linkText} numberOfLines={1}>{u}</Text>
          <Icon name="arrow-up-right-from-square" size={14} color={colors.text_color_tertiary} />
        </Pressable>
      ))}
    </ChatDetailsPageView>
  );

  const renderSavedView = () => (
    <ChatDetailsPageView
      ScrollComponent={ScrollComponent}
      styles={styles}
      emptyText={t('chatDetails.noSavedMessagesFound')}
      isEmpty={!savedMessages || savedMessages.length === 0}
    >
      {(savedMessages || []).length > 0 ? (
        savedMessages.map((m, idx) => (
          <Pressable
            key={(m.id || 'saved') + '_' + idx}
            style={({ hovered }) => [
              styles.savedItem,
              hovered && { backgroundColor: colors.solid_color_secondary }
            ]}
            onPress={() => {
              if (!m?.id) return;
              const theConvoId = conversationId || chat?.id;
              if (theConvoId) {
                setHighlightedMessageId(m.id, searchQuery.trim());
                requestScrollToMessage({ conversationId, messageId: m.id });
              }
            }}
          >
            <Text numberOfLines={6}>{typeof m?.content === 'string' ? m.content : t('chatDetails.nonTextMessage')}</Text>
          </Pressable>
        ))
      ) : null}
    </ChatDetailsPageView>
  );

  const renderMainView = () => (
    <ScrollComponent style={styles.content} contentContainerStyle={styles.scrollContent}>
      {/* Search */}
      <View style={styles.searchContainer}>
        <View style={styles.searchIcon}>
          <Icon 
            name="search" 
            size={14} 
            color={colors.text_color_tertiary}
          />
        </View>
        <TextInput
          style={styles.searchInput}
          placeholder={t('chatDetails.searchPlaceholder')}
          placeholderTextColor={colors.color_input_placeholder}
          value={searchQuery}
          onChangeText={setSearchQuery}
        />
      </View>

      {normalizedQuery ? (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t('chatDetails.messages')}</Text>
          <View style={styles.sectionContent}>
            {searchResults.map((m, idx) => (
              <Pressable
                key={(m.id || 'msg') + '_' + idx}
                style={({ hovered }) => [
                  styles.savedItem,
                  hovered && { backgroundColor: colors.solid_color_secondary }
                ]}
                onPress={() => {
                  if (!m?.id) return;
                  const theConvoId = conversationId || chat?.id;
                  if (theConvoId) {
                    setHighlightedMessageId(m.id, searchQuery.trim());
                    requestScrollToMessage({ conversationId: theConvoId, messageId: m.id });
                  }
                }}
              >
                <Text numberOfLines={6}>{m.content}</Text>
              </Pressable>
            ))}
            {searchResults.length === 0 && (
              <View style={{ padding: 12 }}>
                <Text color='tertiary'>{t('chatDetails.noMessagesFound')}</Text>
              </View>
            )}
          </View>
        </View>
      ) : (
        <>
          {(Platform.OS === 'ios' || Platform.OS === 'android') && <ProjectChangesSection conversationId={conversationId} />}
          {/* Media Buttons */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>{t('chatDetails.media')}</Text>
            <View style={styles.sectionContent}>
              <MediaButton 
                icon="image"
                title={t('chatDetails.photos')}
                rightText={photos.length}
                onPress={() => setActiveView('photos')}
                isModal={isModal}
              />

            <MediaButton 
                icon="video"
                title={t('chatDetails.videos')}
              rightText={videos.length}
              onPress={() => setActiveView('videos')}
              isModal={isModal}
            />

              <MediaButton 
                icon="music"
                title={t('chatDetails.audio')}
                rightText={audios.length}
                onPress={() => setActiveView('audio')}
              isModal={isModal}
            />

              <MediaButton 
                icon="link"
                title={t('chatDetails.links')}
                rightText={links.length}
                onPress={() => setActiveView('links')}
                isModal={isModal}
              />

              <MediaButton 
                icon="files"
                title={Platform.OS === 'macos' ? 'Chat attachments' : t('chatDetails.files')}
                rightText={files.length}
                onPress={() => setActiveView('files')}
                isModal={isModal}
              />

              <MediaButton 
                icon="bookmark"
                title={t('chatDetails.savedMessages')}
                rightText={savedCount}
                onPress={() => setActiveView('saved')}
                isModal={isModal}
              />
            </View>
          </View>

          {/* Info */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>{t('chatDetails.information')}</Text>
            <View style={[styles.sectionContent, { backgroundColor: 'transparent', borderWidth: 0 }]}>
              <MediaButton 
                title={t('chatDetails.created')}
                rightText={createdAt ? createdAt.toLocaleString() : '-'}
                isModal={isModal}
                variant="list"
              />
              <MediaButton 
                title={t('chatDetails.messages')}
                rightText={messagesCount}
                isModal={isModal}
                variant="list"
                isLastItem={modelsUsed.length === 0}
              />
              {modelsUsed.length > 0 && (
                <MediaButton 
                  icon="sparkles"
                  title={t('chatDetails.modelsUsed')}
                  rightText={modelsUsed.length}
                  isModal={isModal}
                  variant="list"
                  isLastItem
                />
              )}
            </View>
          </View>
        </>
      )}

    </ScrollComponent>
  );

  // On native, show the sidebar header even in modal mode (bottom sheet needs
  // back/close buttons). On web, the modal layout provides its own chrome.
  const showHeader = !isModal || Platform.OS !== 'web';

  return (
    <View style={styles.container}>
      {showHeader && (!hideHeader || activeView !== 'main') && (
        <ContextualSidebarHeader 
          title={breadcrumbTitle} 
          onClose={onClose} 
          onBack={activeView !== 'main' ? () => setActiveView('main') : undefined}
        />
      )}
      {activeView === 'main' && renderMainView()}
      {activeView === 'photos' && renderPhotosView()}
      {activeView === 'videos' && renderVideosView()}
      {activeView === 'audio' && renderAudiosView()}
      {activeView === 'files' && renderFilesView()}
      {activeView === 'links' && renderLinksView()}
      {activeView === 'saved' && renderSavedView()}
    </View>
  );
} 

export default ChatDetailsSidebar;
